import { LoadingLogo } from "$components/loading/loading-logo";
import { usePatientContext } from "$routes/_lib/patient-context";
import { gql } from "@apollo/client";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { linkToPatientANCEncounter } from "../links";
import {
  type AncEncounterContextQuery,
  useAncEncounterContextQuery,
  useCreateAncEncounterContextMutation,
} from "./__generated__/anc-encounter-context.gql";

gql`
  mutation CreateAncEncounterContext($input: CreateAncEncounterInput!) {
    createAncEncounter(input: $input) {
      ...AncEncounterFragment
    }
  }
`;

gql`
  query AncEncounterContext($input: QueryAncEncounterInput!) {
    ancEncounter(input: $input) {
      ...AncEncounterFragment
    }
  }
`;

/**
 * This function produces a url for an existing encounter with the encounterId
 * as a url param with any existing search params appended to the end of it
 */
export function useLinkToExistingAntenatalCarePatient() {
  const [searchParams] = useSearchParams();
  return React.useCallback(
    (encounterId: string) =>
      `${linkToPatientANCEncounter(encounterId)}?${searchParams}`,
    [searchParams],
  );
}

/**
 * This context is intended to wrap all ANC Encounter routes.
 * It is responsible for creating an encounter as soon as the user enter the flow.
 * If the user already has an ongoing encounter then that one should be 'resumed' and a new one
 * should not be created.
 * The encounter is required to exist before we can attach any investigations/prescriptions
 * to it.
 */
// Exported so tests can wrap with a stub encounter value rather than mounting
// the full AncEncounterProvider (which performs a GraphQL query).
export const AncEncounterContext =
  React.createContext<AncEncounterContextQuery["ancEncounter"]>(null);

export const AncEncounterProvider: FCwC = ({ children }) => {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const { uuid } = usePatientContext();
  const getLinkToExistingEncounter = useLinkToExistingAntenatalCarePatient();

  const [createEncounter, { data: createData }] =
    useCreateAncEncounterContextMutation();

  // First, get the encounter if we have an id in the url
  const { data: queryData } = useAncEncounterContextQuery({
    variables: { input: { id: encounterId } },
    skip: !encounterId,
  });

  const createEmptyEncounter = React.useCallback(() => {
    createEncounter({
      variables: { input: { patientUuid: uuid } },
    });
  }, [uuid, createEncounter]);

  // If we do not have an encounter id in the url then we need to create one...
  React.useEffect(() => {
    if (encounterId) return;
    createEmptyEncounter();
  }, [createEmptyEncounter, encounterId]);

  // Whether we have created a new encounter or fetched an existing one
  // - get the encounter and update the url if needed
  const encounter = React.useMemo(() => {
    if ((queryData || createData) && !encounterId) {
      const { id } =
        (queryData?.ancEncounter || createData?.createAncEncounter) ?? {};
      navigate(`${getLinkToExistingEncounter(id)}`, { replace: true });
    }
    return (queryData?.ancEncounter || createData?.createAncEncounter) ?? null;
  }, [
    queryData,
    createData,
    navigate,
    encounterId,
    getLinkToExistingEncounter,
  ]);

  // TODO: Contexts _should_ avoid blocking render
  if (!encounter) {
    return <LoadingLogo />;
  }

  return (
    <AncEncounterContext.Provider value={encounter}>
      {children}
    </AncEncounterContext.Provider>
  );
};

export const useAncEncounterContext = () => {
  const context = React.useContext(AncEncounterContext);

  if (context === undefined) {
    throw new Error(
      "useAncEncounter must be used within a AncEncounterProvider",
    );
  }

  return context;
};
